import React from 'react';
import { useAppSelector } from 'store/hooks';
import { Box } from '@mui/material';
import { motion } from 'motion/react';
import {
  selectToolbarPlacement,
  selectToolbarVisibility,
  selectToolbarWidth,
} from 'features/toolbar/toolbarSlice';
import { selectIsSidePanelOpen } from 'features/side-panels/sidePanelsSlice';
import { styled } from '@mui/material/styles';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { ToolbarPlacement } from 'features/toolbar/types';
import Toolbar from 'features/toolbar/toolbar/Toolbar';
import { SystemSessionGuard } from 'components/SystemSessionGuard';
import { styleConstants } from 'theme/styleConstants';

const toolbarPanelWidth = styleConstants.toolbarPanel.width;

const toolbarPaddingSide: Record<ToolbarPlacement, string> = {
  left: 'paddingLeft',
  right: 'paddingRight',
  bottom: 'paddingBottom',
};

const getAnimatedPadding = (
  toolbarWidth: number,
  placement: ToolbarPlacement,
  panelOpen: boolean
) => {
  const paddings: Record<ToolbarPlacement, { paddingLeft: string; paddingRight: string }> = {
    left: {
      paddingLeft: `${toolbarWidth + (panelOpen ? toolbarPanelWidth : 0)}px`,
      paddingRight: '0px',
    },
    right: {
      paddingLeft: '0px',
      paddingRight: `${toolbarWidth + (panelOpen ? toolbarPanelWidth : 0)}px`,
    },
    bottom: {
      paddingLeft: panelOpen ? `${toolbarPanelWidth}px` : '0px',
      paddingRight: '0px',
    },
  };

  return paddings[placement];
};

const springTransition = { type: 'spring' as const, bounce: 0, duration: 0.3 };

const MotionBox = motion.create(Box);

const Root = styled('div')({
  width: '100%',
  overflow: 'hidden',
  position: 'relative',
  height: `calc(var(--app-height) - var(--topbar-height))`,
});

const Content = styled(MotionBox, {
  shouldForwardProp: (prop) => prop !== 'toolbarPlacement',
})<{ toolbarPlacement: ToolbarPlacement }>(({ toolbarPlacement, theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  [toolbarPaddingSide[toolbarPlacement]]: theme.toolbar.width,
}));

const RoomLayoutWrapper = ({ children }: { children: React.ReactNode }) => {
  const panelOpen = useAppSelector(selectIsSidePanelOpen);

  const toolbarPlacement = useAppSelector(selectToolbarPlacement);
  const toolbarWidth = useAppSelector(selectToolbarWidth);
  const toolbarVisible = useAppSelector(selectToolbarVisibility);

  const { isMobile } = useBreakpoints();
  const animatedPadding = getAnimatedPadding(toolbarWidth, toolbarPlacement, panelOpen);

  return (
    <Root>
      {toolbarVisible && (
        <SystemSessionGuard>
          <Toolbar />
        </SystemSessionGuard>
      )}
      <Content
        {...(!isMobile && {
          animate: animatedPadding,
          transition: springTransition,
        })}
        toolbarPlacement={toolbarPlacement}
      >
        {children}
      </Content>
    </Root>
  );
};

export default RoomLayoutWrapper;
